import { useCallback, useEffect, useRef, useState } from 'react';

export const CRM_NAV_DOCK_EDGES = ['left', 'bottom'];

export function navDockEdgeStorageKey(username) {
  return `crmNavDockEdge:${username || 'anon'}`;
}

export function readNavDockEdgePref(username) {
  if (typeof window === 'undefined') return 'left';
  try {
    const stored = localStorage.getItem(navDockEdgeStorageKey(username));
    if (stored === 'left' || stored === 'bottom') return stored;
  } catch (_) {
    // ignore storage errors
  }
  return 'left';
}

export function navDockLeftExpandedFixedStorageKey(username) {
  return `crmNavDockLeftExpandedFixed:${username || 'anon'}`;
}

export function readNavDockLeftExpandedFixedPref(username) {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(navDockLeftExpandedFixedStorageKey(username)) === '1';
  } catch (_) {
    return false;
  }
}

export function writeNavDockLeftExpandedFixedPref(username, fixed) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(navDockLeftExpandedFixedStorageKey(username), fixed ? '1' : '0');
  } catch (_) {
    // ignore storage errors
  }
}

function normalizeDockEdge(edge) {
  if (edge === 'top' || edge === 'right') return 'left';
  return edge === 'bottom' ? 'bottom' : 'left';
}

function resolveSnapEdge(clientX, clientY) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const distances = {
    left: clientX,
    bottom: vh - clientY
  };
  return CRM_NAV_DOCK_EDGES.reduce((best, edge) =>
    distances[edge] < distances[best] ? edge : best
  , 'left');
}

export function useCrmNavDock({ enabled = false, initialEdge = 'left', onEdgeChange } = {}) {
  const [dockEdge, setDockEdgeState] = useState(() => normalizeDockEdge(initialEdge));
  const [isDragging, setIsDragging] = useState(false);
  const [previewEdge, setPreviewEdge] = useState(null);
  const dragRef = useRef(null);
  const onEdgeChangeRef = useRef(onEdgeChange);

  onEdgeChangeRef.current = onEdgeChange;

  useEffect(() => {
    setDockEdgeState(normalizeDockEdge(initialEdge));
  }, [initialEdge]);

  const setDockEdge = useCallback((edge) => {
    const normalized = normalizeDockEdge(edge);
    setDockEdgeState((prev) => {
      if (prev === normalized) return prev;
      onEdgeChangeRef.current?.(normalized);
      return normalized;
    });
  }, []);

  const startDrag = useCallback(
    (clientX, clientY) => {
      if (!enabled) return;
      dragRef.current = { startX: clientX, startY: clientY };
      setIsDragging(true);
      setPreviewEdge(normalizeDockEdge(dockEdge));
      document.body.classList.add('crm-nav-dock-dragging');
    },
    [enabled, dockEdge]
  );

  const stopDrag = useCallback(() => {
    dragRef.current = null;
    setIsDragging(false);
    setPreviewEdge(null);
    document.body.classList.remove('crm-nav-dock-dragging');
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const onPointerMove = (event) => {
      if (!dragRef.current) return;
      setPreviewEdge(resolveSnapEdge(event.clientX, event.clientY));
    };

    const onPointerUp = (event) => {
      if (!dragRef.current) return;
      setDockEdge(resolveSnapEdge(event.clientX, event.clientY));
      stopDrag();
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      document.body.classList.remove('crm-nav-dock-dragging');
    };
  }, [enabled, stopDrag, setDockEdge]);

  const activeEdge = isDragging && previewEdge ? previewEdge : dockEdge;

  return {
    dockEdge: activeEdge,
    isDragging,
    previewEdge: isDragging ? previewEdge : null,
    startDrag,
    setDockEdge
  };
}
